import { useEffect, useState } from 'react';
import { PageContentProcessTabs, FilterBar } from 'framework/ViewTypes/components';
import { TimelineEditorMode, useTimelineEditor } from 'framework/ViewTypes/embeddable';
import PositionsList from './positions/PositionsList';
import PositionProfileTimeline from './timeline/PositionProfileTimeline';
import usePositionProfileTimeline from './timeline/usePositionProfileTimeline';
import { TIMELINE_FILTERS } from './timeline/config';
import usePositionSlotsFilterBar from 'domain/people/positionSlots/preview/List/usePositionSlotsFilterBar';
import usePositionsCollection from './positions/usePositionsCollection';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { POSITION_PROFILE_TIMELINE_TYPES } from '../../constants';
import { ActionIconButton } from 'framework/ui/ui-builder';

type PositionProfileProcessTabsProps = {
  profileId: string;
  canReadTimeline: boolean;
  canManageNotes: boolean;
  canReadPositions: boolean;
};

const POSITION_PROFILE_TAB_ID = {
  POSITIONS: 'positions',
  TIMELINE: 'timeline',
} as const;

const PositionProfileProcessTabs = ({
  profileId,
  canReadTimeline,
  canManageNotes,
  canReadPositions,
}: PositionProfileProcessTabsProps) => {
  const timelineEditor = useTimelineEditor();
  const [activeTabId, setActiveTabId] = useState<string>(POSITION_PROFILE_TAB_ID.TIMELINE);

  const timeline = usePositionProfileTimeline(
    profileId,
    canReadTimeline && activeTabId === POSITION_PROFILE_TAB_ID.TIMELINE,
  );

  const collection = usePositionsCollection(
    profileId,
    canReadPositions && activeTabId === POSITION_PROFILE_TAB_ID.POSITIONS,
  );
  const { filterProps } = usePositionSlotsFilterBar({
    filters: collection.filters,
    setFilters: collection.setFilters,
    resetFilters: collection.resetFilters,
    searchValue: collection.searchValue,
    setSearchValue: collection.setSearchValue,
    excludedFilterIds: ['specializationId', 'technologyId', 'competencyLevel'],
  });

  const timelineTab = {
    id: POSITION_PROFILE_TAB_ID.TIMELINE,
    name: 'Timeline',
    // TODO Need to display the number of unread items.
    // badge: timeline.totalElements,
    panel: (
      <PositionProfileTimeline
        key={profileId}
        canManageNotes={canManageNotes}
        timeline={timeline}
        editor={timelineEditor}
      />
    ),
    actions: [
      <FilterBar
        key="filter"
        sx={{ p: 0 }}
        filterProps={{
          value: timeline.filters,
          onChange: timeline.setFilters,
          onReset: timeline.resetFilters,
          filters: TIMELINE_FILTERS,
        }}
      />,
      <ActionIconButton
        icon={<EditNoteOutlinedIcon />}
        onClick={() => timelineEditor.openAdd(POSITION_PROFILE_TIMELINE_TYPES.NOTE)}
        disabled={!canManageNotes}
        key="add"
        label="Add note"
        tooltip="Add note"
        variant="outlined"
      />,
    ],
  };

  const positionsTab = {
    id: POSITION_PROFILE_TAB_ID.POSITIONS,
    name: 'Positions',
    panel: <PositionsList key={profileId} collection={collection} />,
    actions: [<FilterBar key="filter" sx={{ p: 0 }} filterProps={filterProps} />],
  };

  useEffect(() => {
    if (timeline.isEntryViewMode || timelineEditor.state.mode === TimelineEditorMode.ADD) {
      setActiveTabId(POSITION_PROFILE_TAB_ID.TIMELINE);
    }
  }, [timeline.isEntryViewMode, timelineEditor.state.mode]);

  return (
    <PageContentProcessTabs
      activeTabId={activeTabId}
      onActiveTabChange={setActiveTabId}
      tabs={[...(canReadTimeline ? [timelineTab] : []), ...(canReadPositions ? [positionsTab] : [])]}
    />
  );
};

export default PositionProfileProcessTabs;
